import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useAppDispatch, useAppSelector } from "../../../hooks"
import { getSessionToken, fetchProfile } from "../../../network"
import { setUsdcBalance, setWalletAddress } from "../../../stores/NetworkStore"
import { Modal } from "../modal/modal"
import { useWalletBalance } from "./use-wallet-balance"
import "./withdraw-modal.css"

interface WithdrawModalProps {
  show: boolean
  onClose: () => void
}

export function WithdrawModal({ show, onClose }: WithdrawModalProps) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const walletAddress = useAppSelector((state) => state.network.walletAddress)
  const authMethod = useAppSelector((state) => state.network.authMethod)
  const isPhantom = authMethod === "phantom"
  const { balance, limits, loading: balanceLoading, fetchBalance } = useWalletBalance()

  const [withdrawAmount, setWithdrawAmount] = useState("")
  const [linkWalletAddress, setLinkWalletAddress] = useState("")
  const [linking, setLinking] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [initialLoading, setInitialLoading] = useState(true)

  useEffect(() => {
    if (show) {
      setError(null)
      setSuccess(null)
      setWithdrawAmount("")
      setLinkWalletAddress("")
      setInitialLoading(true)
      fetchBalance().finally(() => setInitialLoading(false))
    }
  }, [show])

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount)
    if (isNaN(amount) || amount <= 0) {
      setError(t("invalid_amount"))
      return
    }
    if (amount > balance) {
      setError(t("insufficient_balance"))
      return
    }

    setProcessing(true)
    setError(null)
    setSuccess(null)

    try {
      const token = getSessionToken()
      const response = await fetch("/wallet/withdraw", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ amount })
      })

      if (response.ok) {
        const data = await response.json()
        const sigDisplay = data.signature ? data.signature.slice(0, 8) + "..." : "pending"
        setSuccess(t("withdrawal_initiated", { signature: sigDisplay }))
        if (data.newBalance !== undefined) {
          dispatch(setUsdcBalance(data.newBalance))
        }
        setWithdrawAmount("")
        fetchBalance()
      } else {
        const err = await response.json()
        setError(err.error || t("withdrawal_failed"))
      }
    } catch (e: unknown) {
      setError(t("withdrawal_failed"))
    } finally {
      setProcessing(false)
    }
  }

  const handleLinkWallet = async () => {
    if (!linkWalletAddress) return
    setLinking(true)
    setError(null)
    setSuccess(null)

    try {
      const token = getSessionToken()
      const response = await fetch("/wallet/link", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ walletAddress: linkWalletAddress })
      })

      if (response.ok) {
        setSuccess(t("wallet_linked"))
        dispatch(setWalletAddress(linkWalletAddress))
        setLinkWalletAddress("")
        fetchProfile(true)
        fetchBalance()
      } else {
        const err = await response.json()
        setError(err.error || t("wallet_link_failed"))
      }
    } catch (e) {
      setError(t("wallet_link_failed"))
    } finally {
      setLinking(false)
    }
  }

  return (
    <Modal
      show={show}
      onClose={onClose}
      header={t("withdraw")}
      className="withdraw-modal"
      body={
        initialLoading ? (
          <p className="withdraw-modal-loading">{t("loading")}</p>
        ) : (
          <div className="withdraw-modal-content">
            <div className="withdraw-modal-balance my-box">
              <span className="withdraw-balance-label">{t("your_balance")}</span>
              <span className="withdraw-balance-value">${balance.toFixed(2)} USDC</span>
            </div>

            {error && <div className="wallet-error">{error}</div>}
            {success && <div className="wallet-success">{success}</div>}

            {walletAddress ? (
              <div className="withdraw-section">
                <h3>{t("withdraw")}</h3>
                <div className="withdraw-wallet-display my-box">
                  <span className="withdraw-wallet-label">
                    {isPhantom ? "Phantom Wallet" : t("linked_wallet")}
                  </span>
                  <code className="withdraw-wallet-address">
                    {walletAddress.slice(0, 6)}...{walletAddress.slice(-6)}
                  </code>
                </div>
                <p className="withdrawal-limit-info">
                  Minimum withdrawal is $1
                </p>
                {limits && (
                  <p className="withdrawal-limit-info">
                    {t("withdrawal_limit_info", {
                      maxPerTx: limits.maxWithdrawalPerTx.toLocaleString(),
                      maxDaily: limits.maxDailyWithdrawal.toLocaleString()
                    })}
                  </p>
                )}
                <div className="withdraw-form-row">
                  <input
                    type="number"
                    placeholder={t("amount_usdc")}
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    step="0.01"
                    min="1"
                    max={balance}
                  />
                  <button
                    className={`bubbly red${processing ? " loading" : ""}`}
                    onClick={handleWithdraw}
                    disabled={processing || balance <= 0}
                  >
                    {processing ? t("processing") : t("withdraw")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="withdraw-section">
                <h3>{t("link_wallet")}</h3>
                <p className="link-wallet-info">{t("link_wallet_description")}</p>
                <div className="link-wallet-form-row">
                  <input
                    type="text"
                    placeholder={t("solana_wallet_address")}
                    value={linkWalletAddress}
                    onChange={(e) => setLinkWalletAddress(e.target.value)}
                  />
                  <button
                    className={`bubbly green${linking ? " loading" : ""}`}
                    onClick={handleLinkWallet}
                    disabled={linking || !linkWalletAddress}
                  >
                    {linking ? t("processing") : t("link_wallet")}
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      }
    />
  )
}
